/**
 * My Tasks — Personal task manager for all APX Hub users
 * Visual language: Warm editorial — light beige bg, stat boxes, inline action links
 * Matches the "PERSONAL TASKS / MY TASKS" mockup design
 */
import { useState, useMemo, useRef, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Plus, CheckCircle2, Circle, Clock, AlertTriangle,
  ChevronDown, ChevronUp, Trash2, UserPlus, X,
  MessageSquare, Send, Pencil, Search,
} from "lucide-react";

// ─── Team members ─────────────────────────────────────────────────────────────
const FALLBACK_TEAM_MEMBERS = [
  { name: "Mr. B — Director",           email: "byjosiah@gmail.com" },
  { name: "Gus G. — Controller",         email: "gus@bbnfi.com" },
  { name: "Amy B. — Controller",         email: "amybyfield@gmail.com" },
  { name: "Jasmin — Tax Division",       email: "jasminsilva91@gmail.com" },
  { name: "Jany L. — Bkkpr Mgr",        email: "lopez.jany@gmail.com" },
  { name: "Irit P. — Bkkpr Supervisor",  email: "iritna@gmail.com" },
  { name: "Yunuen T. — Bkkpr",          email: "yunuenalfaro7@gmail.com" },
  { name: "Shobhit A. — Bkkpr",         email: "shobhit@advancefinserv.com" },
  { name: "Lulu M. — Bkkpr",            email: "lourdesmastache20@gmail.com" },
  { name: "Marian A. — Bkkpr",          email: "marianjoyhernandez2@gmail.com" },
  { name: "Renata L. — Bkkpr",          email: "erenatalazo@gmail.com" },
  { name: "Arvind K. — Bkkpr",          email: "arvind@advancefinserv.com" },
  { name: "Mirian H. — Bkkpr",          email: "mirian@apxhqhub.com" },
  { name: "Grecia S. — Asst Controller", email: "grecia.ssoto13@gmail.com" },
  { name: "Anabel D. — Asst Controller", email: "anabelldiazcoronado@msn.com" },
  { name: "Diana G. — Tax Division",     email: "team@mfisinc.com" },
  { name: "Luna J. — Tax Division",      email: "htminfo@gmail.com" },
];

type TeamMember = { name: string; email: string };

type Priority = "low" | "medium" | "high" | "urgent";
type Status   = "todo" | "in_progress" | "done";

interface Participant {
  userEmail: string;
  userName:  string;
  role:      "participant" | "inform";
}

function formatDate(ts: number | Date | null | undefined): string {
  if (!ts) return "";
  return new Date(ts).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
}

function cap(s: string) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ─── Updates Thread ────────────────────────────────────────────────────────────
function UpdatesThread({ taskId, currentEmail, currentName }: {
  taskId: number; currentEmail: string; currentName: string;
}) {
  const utils = trpc.useUtils();
  const [text, setText] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: updates = [], isLoading } = trpc.myTasks.getUpdates.useQuery(
    { taskId }, { refetchInterval: 15000 }
  );
  const addUpdate = trpc.myTasks.addUpdate.useMutation({
    onSuccess: () => { utils.myTasks.getUpdates.invalidate({ taskId }); setText(""); },
    onError: (err) => toast.error("Failed to post update: " + err.message),
  });
  const deleteUpdate = trpc.myTasks.deleteUpdate.useMutation({
    onSuccess: () => utils.myTasks.getUpdates.invalidate({ taskId }),
    onError: () => toast.error("Failed to delete update"),
  });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [updates.length]);

  function handleSend() {
    const content = text.trim();
    if (!content) return;
    addUpdate.mutate({ taskId, content, authorEmail: currentEmail, authorName: currentName });
  }

  return (
    <div className="mt-3 pt-3 border-t border-stone-200">
      <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-2 flex items-center gap-1.5">
        <MessageSquare className="w-3.5 h-3.5" />
        Updates
        {updates.length > 0 && (
          <span className="ml-1 bg-amber-100 text-amber-700 rounded-full px-2 py-0 text-[10px] font-bold">
            {updates.length}
          </span>
        )}
      </p>
      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
        {isLoading && <div className="h-8 bg-stone-100 rounded animate-pulse" />}
        {!isLoading && updates.length === 0 && (
          <p className="text-xs text-stone-400 italic">No updates yet.</p>
        )}
        {updates.map((u: any) => {
          const isAuthor = u.authorEmail?.toLowerCase() === currentEmail.toLowerCase();
          return (
            <div key={u.id} className={`flex gap-2 group ${isAuthor ? "flex-row-reverse" : ""}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5 ${
                isAuthor ? "bg-amber-600 text-white" : "bg-stone-200 text-stone-600"
              }`}>
                {(u.authorName || "?")[0].toUpperCase()}
              </div>
              <div className={`flex-1 min-w-0 ${isAuthor ? "items-end" : "items-start"} flex flex-col`}>
                <div className={`rounded-lg px-3 py-1.5 text-xs leading-relaxed max-w-[90%] ${
                  isAuthor
                    ? "bg-amber-600 text-white"
                    : "bg-stone-100 text-stone-700 border border-stone-200"
                }`}>
                  {u.content}
                </div>
                <div className={`flex items-center gap-1 mt-0.5 ${isAuthor ? "flex-row-reverse" : ""}`}>
                  <span className="text-[10px] text-stone-400">{u.authorName}</span>
                  <span className="text-[10px] text-stone-300">·</span>
                  <span className="text-[10px] text-stone-400">{formatDate(u.createdAt)}</span>
                  {isAuthor && (
                    <button
                      onClick={() => deleteUpdate.mutate({ id: u.id })}
                      className="text-[10px] text-stone-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                    >✕</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div className="flex gap-2 mt-2">
        <input
          placeholder="Add an update…"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          className="flex-1 h-9 rounded-lg border border-stone-200 bg-white px-3 text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
        <button
          onClick={handleSend}
          disabled={!text.trim() || addUpdate.isPending}
          className="h-9 w-9 rounded-lg bg-amber-600 text-white flex items-center justify-center hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed shrink-0 transition-colors"
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Task Row ──────────────────────────────────────────────────────────────────
function TaskRow({ task, currentEmail, currentName, teamMembers, initialExpanded = false, onStatusChange, onDelete }: {
  task: any;
  currentEmail: string;
  currentName: string;
  teamMembers: TeamMember[];
  initialExpanded?: boolean;
  onStatusChange: (id: number, status: Status) => void;
  onDelete: (id: number) => void;
}) {
  const utils = trpc.useUtils();
  const [showUpdates,     setShowUpdates]     = useState(false);
  const [showDetails,     setShowDetails]     = useState(initialExpanded);
  const [showAddMember,   setShowAddMember]   = useState(false);
  const [addEmail,        setAddEmail]        = useState("");
  const [addRole,         setAddRole]         = useState<"participant" | "inform">("participant");
  const [showEdit,        setShowEdit]        = useState(false);
  const [editTitle,       setEditTitle]       = useState(task.title ?? "");
  const [editDescription, setEditDescription] = useState(task.description ?? "");
  const [editPriority,    setEditPriority]    = useState<Priority>(task.priority ?? "medium");
  const [editDueAt,       setEditDueAt]       = useState(
    task.dueAt ? new Date(task.dueAt).toISOString().split("T")[0] : ""
  );

  const { user: authUser } = useAuth();
  const liveEmail   = (authUser?.email    ?? currentEmail ?? "").toLowerCase().trim();
  const liveName    = (authUser?.name     ?? currentName  ?? "").toLowerCase().trim();
  const storedEmail = (task.createdBy     ?? "").toLowerCase().trim();
  const storedName  = (task.createdByName ?? "").toLowerCase().trim();
  const isOwner = (storedEmail !== "" && storedEmail === liveEmail)
               || (storedName  !== "" && storedName  === liveName);

  const updateTask = trpc.myTasks.update.useMutation({
    onSuccess: () => { utils.myTasks.list.invalidate(); toast.success("Task updated"); setShowEdit(false); },
    onError: (err) => toast.error("Failed to update: " + err.message),
  });
  const addParticipant = trpc.myTasks.addParticipant.useMutation({
    onSuccess: () => {
      utils.myTasks.list.invalidate();
      toast.success("Teammate added");
      setAddEmail(""); setAddRole("participant"); setShowAddMember(false);
    },
    onError: (err) => toast.error("Failed to add teammate: " + err.message),
  });
  const removeParticipant = trpc.myTasks.removeParticipant.useMutation({
    onSuccess: () => utils.myTasks.list.invalidate(),
    onError: () => toast.error("Failed to remove teammate"),
  });

  const existingEmails = new Set((task.participants ?? []).map((p: Participant) => p.userEmail.toLowerCase()));
  const availableToAdd = teamMembers.filter(
    m => m.email.toLowerCase() !== liveEmail && !existingEmails.has(m.email.toLowerCase())
  );

  const isDone    = task.status === "done";
  const isOverdue = task.dueAt && new Date(task.dueAt) < new Date() && !isDone;

  const nextStatus: Record<Status, Status> = { todo: "in_progress", in_progress: "done", done: "todo" };

  const priorityLabel: Record<string, string> = {
    low: "LOW", medium: "MEDIUM", high: "HIGH", urgent: "URGENT",
  };
  const priorityColor: Record<string, string> = {
    low:    "text-stone-500 font-semibold text-xs tracking-widest",
    medium: "text-amber-700 font-semibold text-xs tracking-widest",
    high:   "text-orange-600 font-semibold text-xs tracking-widest",
    urgent: "text-red-600 font-semibold text-xs tracking-widest",
  };

  const statusLabel: Record<string, string> = {
    todo: "TO DO", in_progress: "IN PROGRESS", done: "DONE",
  };

  return (
    <div className={`rounded-xl border transition-all ${
      isDone
        ? "bg-stone-50 border-stone-200 opacity-70"
        : isOverdue
        ? "bg-orange-50 border-orange-200"
        : "bg-white border-stone-200"
    }`}>
      <div className="flex items-start gap-3 px-5 py-4">
        {/* Status circle toggle */}
        <button
          onClick={() => onStatusChange(task.id, nextStatus[task.status as Status])}
          className="mt-0.5 shrink-0 hover:scale-110 transition-transform"
          title={`Mark as ${nextStatus[task.status as Status]}`}
        >
          {isDone
            ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            : task.status === "in_progress"
            ? <Clock className="w-5 h-5 text-amber-500" />
            : <Circle className="w-5 h-5 text-stone-400" />
          }
        </button>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Title + priority */}
          <div className="flex items-start justify-between gap-3">
            <p className={`font-bold text-[15px] leading-snug ${
              isDone ? "line-through text-stone-400" : "text-stone-900"
            }`}>
              {task.title}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <span className={priorityColor[task.priority] ?? priorityColor.medium}>
                {priorityLabel[task.priority] ?? "MEDIUM"}
              </span>
              {isOwner && (
                <button
                  onClick={() => onDelete(task.id)}
                  className="text-stone-300 hover:text-red-500 transition-colors"
                  title="Delete task"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Meta row: status pill + date + overdue + by */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2">
            {/* Status pill */}
            <span className={`inline-flex items-center gap-1 text-[11px] font-bold tracking-widest uppercase px-2 py-0.5 rounded border ${
              task.status === "done"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : task.status === "in_progress"
                ? "bg-amber-50 text-amber-700 border-amber-200"
                : "bg-stone-100 text-stone-600 border-stone-200"
            }`}>
              <Circle className="w-2.5 h-2.5" />
              {statusLabel[task.status] ?? "TO DO"}
            </span>

            {/* Due date */}
            {task.dueAt && (
              <span className={`flex items-center gap-1 text-xs font-medium ${
                isOverdue ? "text-orange-600" : "text-stone-500"
              }`}>
                <Clock className="w-3 h-3" />
                {formatDate(task.dueAt)}
              </span>
            )}

            {/* Overdue warning */}
            {isOverdue && (
              <span className="flex items-center gap-1 text-xs font-bold text-orange-600">
                <AlertTriangle className="w-3 h-3" />
                Overdue
              </span>
            )}

            {/* Creator */}
            {task.createdByName && (
              <span className="text-xs text-stone-400">By {task.createdByName}</span>
            )}
          </div>

          {/* Participants */}
          {task.participants && task.participants.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {task.participants.map((p: Participant) => (
                <span key={p.userEmail} className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                  p.role === "inform"
                    ? "bg-stone-100 text-stone-500 border border-stone-200"
                    : "bg-amber-50 text-amber-700 border border-amber-200"
                }`}>
                  {p.userName}{p.role === "inform" ? " (info)" : ""}
                  {isOwner && (
                    <button
                      onClick={() => removeParticipant.mutate({ taskId: task.id, userEmail: p.userEmail })}
                      className="hover:text-red-500 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}

          {/* Action links row */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
            {/* Details */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 transition-colors"
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showDetails ? "rotate-180" : ""}`} />
              Details
            </button>

            {/* Updates */}
            <button
              onClick={() => setShowUpdates(!showUpdates)}
              className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 transition-colors"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Updates
            </button>

            {/* Edit */}
            {isOwner && (
              <button
                onClick={() => setShowEdit(true)}
                className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
            )}

            {/* Add Teammate */}
            <button
              onClick={() => setShowAddMember(!showAddMember)}
              className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 transition-colors"
            >
              <UserPlus className="w-3.5 h-3.5" />
              + Add Teammate
            </button>
          </div>

          {/* Details expand */}
          {showDetails && task.description && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-1">Description</p>
              <p className="text-sm text-stone-600 leading-relaxed">{task.description}</p>
            </div>
          )}
          {showDetails && !task.description && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <p className="text-xs text-stone-400 italic">No description provided.</p>
            </div>
          )}

          {/* Add teammate inline */}
          {showAddMember && (
            <div className="mt-3 pt-3 border-t border-stone-100 flex gap-2 flex-wrap items-center">
              <select
                value={addEmail}
                onChange={e => setAddEmail(e.target.value)}
                className="flex-1 min-w-[180px] h-8 rounded-lg border border-stone-200 bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
              >
                <option value="">Select teammate…</option>
                {availableToAdd.map(m => (
                  <option key={m.email} value={m.email}>{m.name}</option>
                ))}
              </select>
              <select
                value={addRole}
                onChange={e => setAddRole(e.target.value as "participant" | "inform")}
                className="w-32 h-8 rounded-lg border border-stone-200 bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
              >
                <option value="participant">Participant</option>
                <option value="inform">Inform only</option>
              </select>
              <button
                onClick={() => {
                  if (!addEmail) return;
                  const member = teamMembers.find(m => m.email === addEmail);
                  if (!member) return;
                  addParticipant.mutate({ taskId: task.id, userEmail: member.email, userName: member.name, role: addRole });
                }}
                disabled={!addEmail || addParticipant.isPending}
                className="h-8 px-3 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Add
              </button>
              <button onClick={() => setShowAddMember(false)} className="text-stone-400 hover:text-stone-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Updates thread */}
          {showUpdates && (
            <UpdatesThread
              taskId={task.id}
              currentEmail={currentEmail}
              currentName={currentName}
            />
          )}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={showEdit} onOpenChange={v => { if (!v) setShowEdit(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-bold">Edit Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Title</label>
              <Input value={editTitle} onChange={e => setEditTitle(cap(e.target.value))} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Description</label>
              <Textarea value={editDescription} onChange={e => setEditDescription(cap(e.target.value))} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Priority</label>
                <Select value={editPriority} onValueChange={v => setEditPriority(v as Priority)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Due Date</label>
                <Input type="date" value={editDueAt} onChange={e => setEditDueAt(e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button
              onClick={() => updateTask.mutate({
                id: task.id,
                title: editTitle.trim(),
                description: editDescription.trim() || undefined,
                priority: editPriority,
                dueAt: editDueAt ? new Date(editDueAt) : undefined,
              })}
              disabled={updateTask.isPending}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
            >
              {updateTask.isPending ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Create Task Dialog ────────────────────────────────────────────────────────
function CreateTaskDialog({ open, onClose, currentEmail, currentName, teamMembers }: {
  open: boolean; onClose: () => void; currentEmail: string; currentName: string; teamMembers: TeamMember[];
}) {
  const utils = trpc.useUtils();
  const [title,        setTitle]        = useState("");
  const [description,  setDescription]  = useState("");
  const [priority,     setPriority]     = useState<Priority>("medium");
  const [dueAt,        setDueAt]        = useState("");
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [addingEmail,  setAddingEmail]  = useState("");
  const [addingRole,   setAddingRole]   = useState<"participant" | "inform">("participant");

  const createMutation = trpc.myTasks.create.useMutation({
    onSuccess: () => { utils.myTasks.list.invalidate(); toast.success("Task created"); onClose(); resetForm(); },
    onError: (err) => toast.error(err.message),
  });

  function resetForm() {
    setTitle(""); setDescription(""); setPriority("medium"); setDueAt("");
    setParticipants([]); setAddingEmail(""); setAddingRole("participant");
  }

  function addParticipant() {
    if (!addingEmail) return;
    const member = teamMembers.find(m => m.email === addingEmail);
    if (!member) return;
    if (participants.find(p => p.userEmail === addingEmail)) { toast.error("Already added"); return; }
    if (addingEmail === currentEmail.toLowerCase()) { toast.error("You are already the task owner"); return; }
    setParticipants(prev => [...prev, { userEmail: member.email, userName: member.name, role: addingRole }]);
    setAddingEmail("");
  }

  function handleSubmit() {
    if (!title.trim()) { toast.error("Task title is required"); return; }
    createMutation.mutate({
      title: title.trim(),
      description: description.trim() || undefined,
      priority,
      dueAt: dueAt ? new Date(dueAt) : undefined,
      callerEmail: currentEmail,
      callerName: currentName,
      participants: participants.length > 0 ? participants : undefined,
    });
  }

  const availableMembers = teamMembers.filter(
    m => m.email !== currentEmail.toLowerCase() && !participants.find(p => p.userEmail === m.email)
  );

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); resetForm(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-bold tracking-wide">Create New Task</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Title *</label>
            <Input
              placeholder="What needs to be done?"
              value={title}
              onChange={e => setTitle(cap(e.target.value))}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Description</label>
            <Textarea
              placeholder="Additional details (optional)"
              value={description}
              onChange={e => setDescription(cap(e.target.value))}
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Priority</label>
              <Select value={priority} onValueChange={v => setPriority(v as Priority)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Due Date</label>
              <Input type="date" value={dueAt} onChange={e => setDueAt(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Tag Teammates</label>
            {participants.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {participants.map(p => (
                  <span key={p.userEmail} className="flex items-center gap-1 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 rounded-full">
                    {p.userName}{p.role === "inform" ? " (info)" : ""}
                    <button onClick={() => setParticipants(prev => prev.filter(x => x.userEmail !== p.userEmail))}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {availableMembers.length > 0 && (
              <div className="flex gap-2 items-center">
                <select
                  value={addingEmail}
                  onChange={e => setAddingEmail(e.target.value)}
                  className="flex-1 h-9 rounded-lg border border-stone-200 bg-white px-3 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
                >
                  <option value="">Select teammate…</option>
                  {availableMembers.map(m => (
                    <option key={m.email} value={m.email}>{m.name}</option>
                  ))}
                </select>
                <select
                  value={addingRole}
                  onChange={e => setAddingRole(e.target.value as "participant" | "inform")}
                  className="w-36 h-9 rounded-lg border border-stone-200 bg-white px-3 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
                >
                  <option value="participant">Participant</option>
                  <option value="inform">Inform only</option>
                </select>
                <button
                  type="button"
                  onClick={addParticipant}
                  disabled={!addingEmail}
                  className="h-9 px-3 rounded-lg border border-stone-200 bg-white text-sm font-medium hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >Add</button>
              </div>
            )}
            <p className="text-xs text-stone-400 mt-1.5">
              Participants can view and update. Inform-only members are notified but cannot edit.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); resetForm(); }}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
          >
            {createMutation.isPending ? "Creating…" : "Create Task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function MyTasks() {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [showCreate,   setShowCreate]   = useState(false);
  // Read ?taskId= URL param to deep-link to a specific task from notification click
  const initialTaskId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("taskId");
    return raw ? parseInt(raw, 10) : null;
  }, []);
  const [filterStatus, setFilterStatus] = useState<"all" | Status | "overdue">("all");
  const [showDone,     setShowDone]     = useState(false);
  const [search,       setSearch]       = useState("");
  const { data: activeStaff = [] } = trpc.team.list.useQuery();
  const teamMembers: TeamMember[] = activeStaff.length > 0
    ? activeStaff.map((member: { name: string; email: string }) => ({ name: member.name, email: member.email }))
    : FALLBACK_TEAM_MEMBERS;

  const { data: tasks = [], isLoading } = trpc.myTasks.list.useQuery(
    { userEmail: user?.email ?? "", isAdmin: false },
    { enabled: !!user?.email, refetchInterval: 10000 }
  );

  const updateStatus = trpc.myTasks.updateStatus.useMutation({
    onSuccess: () => utils.myTasks.list.invalidate(),
    onError: (err) => toast.error("Failed to update status: " + err.message),
  });

  const deleteTask = trpc.myTasks.delete.useMutation({
    onSuccess: () => { utils.myTasks.list.invalidate(); toast.success("Task deleted"); },
    onError: (err) => toast.error("Failed to delete task: " + err.message),
  });

  const counts = useMemo(() => ({
    total:       tasks.length,
    todo:        tasks.filter((t: any) => t.status === "todo").length,
    in_progress: tasks.filter((t: any) => t.status === "in_progress").length,
    done:        tasks.filter((t: any) => t.status === "done").length,
    overdue:     tasks.filter((t: any) => t.status !== "done" && t.dueAt && new Date(t.dueAt) < new Date()).length,
  }), [tasks]);

  const activeTasks = useMemo(() => {
    let list = tasks.filter((t: any) => t.status !== "done");
    if (filterStatus === "overdue") {
      list = list.filter((t: any) => t.dueAt && new Date(t.dueAt) < new Date());
    } else if (filterStatus !== "all") {
      list = list.filter((t: any) => t.status === filterStatus);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((t: any) =>
        t.title?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q) ||
        t.createdByName?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [tasks, filterStatus, search]);

  const doneTasks = useMemo(() =>
    tasks.filter((t: any) => t.status === "done"),
    [tasks]
  );

  const displayTasks = filterStatus === "done" ? doneTasks : activeTasks;

  return (
    <DashboardLayout noPadding>
      {/* Page background */}
      <div className="min-h-screen bg-[#f5f0e8]">
        <div className="max-w-5xl mx-auto px-6 py-8">

          {/* ── Section: MY TASKS ── */}
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden mb-6">
            {/* Section header */}
            <div className="px-6 pt-6 pb-4 border-b border-stone-100">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold tracking-[0.25em] uppercase text-stone-400 mb-1">
                    PERSONAL TASKS
                  </p>
                  <h2 className="text-2xl font-black tracking-tight text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                    MY TASKS
                  </h2>
                  <p className="text-xs text-stone-400 mt-0.5">
                    Private — visible only to you and tagged teammates
                  </p>
                </div>
                <button
                  onClick={() => setShowCreate(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm tracking-wide transition-all active:scale-95 shrink-0 mt-1 bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  New Task
                </button>
              </div>

              {/* Stat boxes */}
              <div className="grid grid-cols-3 gap-3 mt-5">
                <button
                  onClick={() => setFilterStatus("todo")}
                  className={`rounded-xl border px-4 py-3 text-left transition-all ${
                    filterStatus === "todo"
                      ? "border-stone-400 bg-stone-100"
                      : "border-stone-200 bg-stone-50 hover:border-stone-300"
                  }`}
                >
                  <p className="text-2xl font-black text-stone-800">{isLoading ? "—" : counts.todo}</p>
                  <p className="text-xs font-bold tracking-widest uppercase text-stone-500 mt-0.5">To Do</p>
                </button>
                <button
                  onClick={() => setFilterStatus("in_progress")}
                  className={`rounded-xl border px-4 py-3 text-left transition-all ${
                    filterStatus === "in_progress"
                      ? "border-amber-400 bg-amber-50"
                      : "border-amber-200 bg-amber-50/50 hover:border-amber-300"
                  }`}
                >
                  <p className="text-2xl font-black text-amber-700">{isLoading ? "—" : counts.in_progress}</p>
                  <p className="text-xs font-bold tracking-widest uppercase text-amber-600 mt-0.5">In Progress</p>
                </button>
                <button
                  onClick={() => setFilterStatus("done")}
                  className={`rounded-xl border px-4 py-3 text-left transition-all ${
                    filterStatus === "done"
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-emerald-200 bg-emerald-50/50 hover:border-emerald-300"
                  }`}
                >
                  <p className="text-2xl font-black text-emerald-700">{isLoading ? "—" : counts.done}</p>
                  <p className="text-xs font-bold tracking-widest uppercase text-emerald-600 mt-0.5">Done</p>
                </button>
              </div>

              {/* Search */}
              <div className="relative mt-4">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="text"
                  placeholder="Search tasks…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition-colors"
                />
              </div>

              {/* Filter tabs (compact) */}
              {filterStatus !== "all" && (
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs text-stone-400">Filtered by:</span>
                  <span className="flex items-center gap-1 text-xs font-bold bg-stone-100 text-stone-600 px-2.5 py-1 rounded-full border border-stone-200">
                    {filterStatus === "todo" ? "To Do" : filterStatus === "in_progress" ? "In Progress" : filterStatus === "done" ? "Done" : "Overdue"}
                    <button onClick={() => setFilterStatus("all")} className="hover:text-red-500 ml-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                </div>
              )}
            </div>

            {/* Task list */}
            <div className="p-5 space-y-3">
              {isLoading ? (
                [1, 2, 3].map(i => (
                  <div key={i} className="h-20 rounded-xl bg-stone-100 animate-pulse" />
                ))
              ) : displayTasks.length === 0 ? (
                <div className="text-center py-12 text-stone-400">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="font-bold text-sm">
                    {search ? "No tasks match your search" : filterStatus === "all" ? "No active tasks" : "No tasks in this category"}
                  </p>
                  {filterStatus === "all" && (
                    <p className="text-xs mt-1">Click "+ New Task" to get started</p>
                  )}
                </div>
              ) : (
                displayTasks.map((task: any) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    currentEmail={user?.email ?? ""}
                    currentName={user?.name ?? ""}
                    teamMembers={teamMembers}
                    initialExpanded={task.id === initialTaskId}
                    onStatusChange={(id, status) => updateStatus.mutate({ id, status, callerEmail: user?.email ?? "", callerName: user?.name ?? "" })}
                    onDelete={(id) => deleteTask.mutate({ id })}
                  />
                ))
              )}

              {/* Completed section (when not on Done tab) */}
              {filterStatus !== "done" && doneTasks.length > 0 && (
                <div className="pt-4 border-t border-stone-100">
                  <button
                    onClick={() => setShowDone(!showDone)}
                    className="flex items-center gap-2 text-xs font-bold text-stone-400 hover:text-stone-600 mb-3 tracking-widest uppercase transition-colors"
                  >
                    {showDone ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    Completed ({doneTasks.length})
                  </button>
                  {showDone && (
                    <div className="space-y-2">
                      {doneTasks.map((task: any) => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          currentEmail={user?.email ?? ""}
                          currentName={user?.name ?? ""}
                          teamMembers={teamMembers}
                          onStatusChange={(id, status) => updateStatus.mutate({ id, status, callerEmail: user?.email ?? "", callerName: user?.name ?? "" })}
                          onDelete={(id) => deleteTask.mutate({ id })}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      <CreateTaskDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        currentEmail={user?.email ?? ""}
        currentName={user?.name ?? ""}
        teamMembers={teamMembers}
      />
    </DashboardLayout>
  );
}
